import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface AreaProps {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
  onBlur?: () => void;
  rows?: number;
  placeholder?: string;
  helper?: string;
}

/** Labeled textarea shared by Anamnesis/Exploración/Plan — extracted so the field
 *  markup (and its save-on-blur wiring from the caller) stays identical across the
 *  card-based section files. */
export function Area(props: AreaProps): JSX.Element {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium">{props.label}</Label>
      <textarea
        className="border-input bg-background min-h-[60px] w-full rounded-md border px-3 py-2 text-sm disabled:opacity-60"
        rows={props.rows ?? 2}
        value={props.value}
        disabled={props.disabled}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        onBlur={props.onBlur}
      />
      {props.helper ? <p className="text-muted-foreground text-[11px]">{props.helper}</p> : null}
    </div>
  );
}

interface NumFieldProps {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
  onBlur?: () => void;
  placeholder?: string;
}

/** Labeled numeric input — same field shape as `Area` for the vitals grid. */
export function NumField(props: NumFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium">{props.label}</Label>
      <Input
        type="number"
        value={props.value}
        disabled={props.disabled}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        onBlur={props.onBlur}
      />
    </div>
  );
}

interface TextFieldProps {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
  onBlur?: () => void;
  placeholder?: string;
}

/** Labeled single-line text input — for short quick-fill EFG fields (as opposed to `Area`'s
 *  multi-line paragraph notes). */
export function TextField(props: TextFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium">{props.label}</Label>
      <Input
        type="text"
        value={props.value}
        disabled={props.disabled}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        onBlur={props.onBlur}
      />
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
  onBlur?: () => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}

/** Labeled native select — for EFG fields with a fixed clinical option set (E. Mental, DH, MM).
 *  Styled to match the diagnosis-kind picker in `DiagnosesSection.tsx`. */
export function SelectField(props: SelectFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium">{props.label}</Label>
      <select
        className="border-input bg-background h-9 w-full rounded-md border px-2 py-1 text-sm disabled:opacity-60"
        value={props.value}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.value)}
        onBlur={props.onBlur}
      >
        <option value="">{props.placeholder ?? ''}</option>
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
